import { apiClient } from './client';
import type {
  SubscriptionPlan,
  CreateSubscriptionPlanRequest,
  UpdateSubscriptionPlanRequest,
  PaginatedSubscriptionPlans,
} from '@repo/types';

export type {
  SubscriptionPlan,
  CreateSubscriptionPlanRequest,
  UpdateSubscriptionPlanRequest,
  PaginatedSubscriptionPlans,
};

/**
 * Subscription plan catalog API (`/api/plans`).
 *
 * Reads are available to any authenticated staff member; writes are restricted
 * to Admin/Manager on the backend, so the UI gates those actions accordingly.
 */
export const plansApi = {
  /** List plans (ordered by tier), paginated. */
  getPlans: async (page = 1, limit = 20): Promise<PaginatedSubscriptionPlans> => {
    const response = await apiClient.get<PaginatedSubscriptionPlans>('/plans', {
      params: { page, limit },
    });
    return response.data;
  },

  /** Get a single plan by ID. */
  getPlan: async (id: string): Promise<SubscriptionPlan> => {
    const response = await apiClient.get<SubscriptionPlan>(`/plans/${id}`);
    return response.data;
  },

  /** Create a plan (Admin/Manager only). */
  createPlan: async (
    data: CreateSubscriptionPlanRequest
  ): Promise<SubscriptionPlan> => {
    const response = await apiClient.post<SubscriptionPlan>('/plans', data);
    return response.data;
  },

  /** Update a plan (Admin/Manager only). */
  updatePlan: async (
    id: string,
    data: UpdateSubscriptionPlanRequest
  ): Promise<SubscriptionPlan> => {
    const response = await apiClient.put<SubscriptionPlan>(`/plans/${id}`, data);
    return response.data;
  },

  /** Move a plan's active subscribers to another plan. Returns how many moved. */
  moveSubscribers: async (id: string, toPlanId: string): Promise<number> => {
    const response = await apiClient.post<{ moved: number }>(`/plans/${id}/move-subscribers`, { toPlanId });
    return response.data.moved;
  },

  /** Delete a plan (Admin/Manager only). Refused while customers are still on it. */
  deletePlan: async (id: string): Promise<void> => {
    await apiClient.delete(`/plans/${id}`);
  },
};
